/**
 * Shared pure utilities for customer form components (Add/Edit).
 * No React imports — plain JS functions only.
 */

export const toCamelCase = (str) => {
  if (!str) return "";
  return str
    .trim()
    .replace(/[^a-zA-Z0-9 ]/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(" ")
    .filter(Boolean)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join("");
};

export const normalizeKey = (str = "") => str.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Re-cases every sub-key of each group_* object in `data` via toCamelCase().
 * Sub-keys are EXPECTED to already be correctly camelCased (Form.Item names
 * are registered as toCamelCase(field.name)), but a group object can also
 * carry a stale raw-label duplicate of the same field (e.g. both "city" and
 * "City") left behind by an unrelated initial-population write that never
 * gets cleaned up. Both keys normalize to the same output key, so a naive
 * single pass that just assigns in enumeration order lets whichever key
 * happens to be visited last win -- which silently discarded a freshly-typed
 * value in favor of a stale one (BUG-2341-class, address-not-persisting RCA,
 * 2026-09-23: confirmed live via AddrDiag submit-form-snapshot trace showing
 * camelCase "city": "Guntur" [correct, just typed] alongside raw-label
 * "City": "Dehradun" [stale], with the stale one reaching the update
 * payload). A key that is already canonical camelCase (toCamelCase(subKey)
 * === subKey) is the live Form.Item value and always wins; a raw-label
 * duplicate is only used to fill in a key that's otherwise missing.
 */
export const formatGroupKeysToCamelCase = (data) => {
  const formatted = { ...data };
  for (const key in formatted) {
    if (key.startsWith("group_")) {
      let value = formatted[key];
      try {
        if (typeof value === "string") {
          try {
            value = JSON.parse(value);
          } catch (err) {
            console.warn(`Invalid JSON in ${key}, skipping parse.`);
            continue;
          }
        }
        if (value && typeof value === "object") {
          const converted = {};
          const fallbacks = [];
          for (const subKey in value) {
            const camelKey = toCamelCase(subKey);
            if (subKey === camelKey) {
              converted[camelKey] = value[subKey];
            } else {
              fallbacks.push([camelKey, value[subKey]]);
            }
          }
          for (const [camelKey, val] of fallbacks) {
            if (!(camelKey in converted)) converted[camelKey] = val;
          }
          formatted[key] = JSON.stringify(converted);
        }
      } catch (err) {
        console.warn(`Error processing ${key}`, err);
      }
    }
  }
  return formatted;
};

export const getStaticDisplayValue = (field, savedKey) => {
  if (!field || !field.defaultValue || !savedKey) return "";
  try {
    const parsed = JSON.parse(field.defaultValue);
    return parsed[savedKey] || savedKey;
  } catch {
    return savedKey;
  }
};

export const normalizeStaticSelectValue = (value, staticJson) => {
  if (!value || !staticJson) return value;
  try {
    const parsed = JSON.parse(staticJson);
    if (parsed[value]) return value;
    const reverseMap = Object.fromEntries(Object.entries(parsed).map(([k, v]) => [v, k]));
    return reverseMap[value] || value;
  } catch {
    return value;
  }
};

export const flattenObject = (obj, prefix = "", res = {}) => {
  for (const key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      flattenObject(obj[key], prefix + key + ".", res);
    } else {
      res[prefix + key] = obj[key];
    }
  }
  return res;
};

export const mapB2BDataDynamic = (customer, groupedFields, isAddMode = false) => {
  flattenObject(customer);
  const formValues = {};
  const safeJoin = (...parts) => parts.filter(Boolean).join(", ").trim();
  const toCamel = (str) =>
    str
      .trim()
      .replace(/[^a-zA-Z0-9 ]/g, " ")
      .split(" ")
      .map((word, index) => (index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
      .join("");

  groupedFields.forEach((group) => {
    const groupId = group.groupId ? String(group.groupId) : null;
    const groupNameLower = group.groupName?.toLowerCase() || "";
    const isInvoiceGroup = groupNameLower.includes("invoice") || groupNameLower.includes("invoicing");
    const isDeliveryGroup = groupNameLower.includes("delivery");
    const address = isInvoiceGroup ? customer.invoiceAddresses?.[0] : isDeliveryGroup ? customer.deliveryAddresses?.[0] : null;
    const matchingLoc = address ? customer.localisation?.find((l) => l.addressId === address.s_customer_address_id) : customer.localisation?.[0] || null;

    group.fields.forEach((field) => {
      if (!field.visible || !field.tabId) return;
      const rawLabel = field.name || "";
      const labelTrimmed = rawLabel.trim();
      const key = labelTrimmed.toLowerCase();
      const fieldKey = toCamel(labelTrimmed);
      let value = null;

      if (address && (isInvoiceGroup || isDeliveryGroup)) {
        if (key.includes("street")) value = safeJoin(address.line1, address.line2);
        else if (key.includes("line1") || key.includes("line 1")) value = address.line1 || "";
        else if (key.includes("line2") || key.includes("line 2")) value = address.line2 || "";
        else if (key.includes("city")) value = address.city || "";
        else if (key.includes("state") || key.includes("region")) value = address.regionName || "";
        else if (key.includes("country")) value = address.countryName || "";
        else if (key.includes("pincode") || key.includes("zip") || key.includes("postal")) value = address.pincode || "";
        else if (key.includes("gst") && matchingLoc) value = matchingLoc.gstType || "";
        else if (key.includes("company type") || (key.includes("company") && key.includes("type"))) value = matchingLoc?.companyType || "";
      } else {
        if (key.includes("customer code")) value = customer.customerCode || "";
        else if (key.includes("customer name")) value = customer.customerName || "";
        else if (key.includes("tax") && key.includes("id")) value = customer.taxId || "";
        else if (key === "company name" || key.includes("company name")) value = customer.companyName || "";
        else if (key === "gst no" || key.includes("gst no")) value = customer.gstNo || customer.localisation?.[0]?.gstNo || "";
        else if (key.includes("company name") || (key.includes("company") && !key.includes("type")))
          value = matchingLoc?.companyType || customer.localisation?.[0]?.companyType || "";
      }

      if (value !== null && value !== undefined && value !== "") {
        if (groupId) {
          formValues[groupId] = formValues[groupId] || {};
          formValues[groupId][rawLabel] = value;
          if (rawLabel !== labelTrimmed) formValues[groupId][labelTrimmed] = value;
          formValues[groupId][fieldKey] = value;
        } else {
          formValues[rawLabel] = value;
          if (rawLabel !== labelTrimmed) formValues[labelTrimmed] = value;
          formValues[fieldKey] = value;
        }
      }
    });
  });

  if (isAddMode) {
    const flattened = {};
    Object.entries(formValues).forEach(([groupId, fields]) => {
      if (typeof fields === "object" && fields !== null) {
        Object.entries(fields).forEach(([label, val]) => {
          flattened[[groupId, label]] = val;
        });
      } else {
        flattened[groupId] = fields;
      }
    });
    return flattened;
  }
  return formValues;
};

export const fillAddressFields = (address, groupOrId, _type, customer, customerForm, activeTab, setFormDataCache) => {
  if (!address || !groupOrId) return;
  const groupId = typeof groupOrId === "object" ? groupOrId.groupId : groupOrId;
  if (!groupId && groupId !== 0) return;

  const safe = (v) => (v === undefined || v === null ? "" : v);
  const toCamel = (str) =>
    String(str || "")
      .replace(/[^a-zA-Z0-9 ]/g, " ")
      .trim()
      .split(/\s+/)
      .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
      .join("");
  const trimLabel = (s) => String(s || "").trim();
  const loc = customer?.localisation?.find((l) => l.addressId === address.s_customer_address_id) || {};

  const rawToValue = {
    "Street Address": safe(address.line1) + (address.line2 ? ", " + safe(address.line2) : ""),
    City: safe(address.city),
    State: safe(address.regionName),
    Country: safe(address.countryName),
    Pincode: safe(address.pincode),
    "GST Type": safe(loc.gstType),
    "Company Type": safe(loc.companyType),
    "GST Number": safe(loc.gstNo),
    "Pan Number": safe(loc.panNo),
  };

  const groupVariants = new Set();
  groupVariants.add(groupId);
  groupVariants.add(String(groupId));
  groupVariants.add(String(groupId).toLowerCase());
  if (!Number.isNaN(Number(groupId))) groupVariants.add(Number(groupId));

  const fieldsPayload = {};
  const canonicalGroupKey = String(groupId);
  const canonicalForCache = {};

  Object.entries(rawToValue).forEach(([rawLabel, val]) => {
    const trimmed = trimLabel(rawLabel);
    const camel = toCamel(trimmed);
    canonicalForCache[rawLabel] = val;
    if (trimmed !== rawLabel) canonicalForCache[trimmed] = val;
    canonicalForCache[camel] = val;
    groupVariants.forEach((gKey) => {
      if (!fieldsPayload[gKey]) fieldsPayload[gKey] = {};
      fieldsPayload[gKey][rawLabel] = val;
      fieldsPayload[gKey][trimmed] = val;
      fieldsPayload[gKey][camel] = val;
    });
  });

  try {
    customerForm.setFieldsValue(fieldsPayload);
  } catch (err) {
    Object.entries(fieldsPayload).forEach(([gKey, obj]) => {
      customerForm.setFieldsValue({ [gKey]: { ...(customerForm.getFieldValue(gKey) || {}), ...obj } });
    });
  }

  setFormDataCache((prev) => ({
    ...prev,
    [activeTab]: {
      ...(prev[activeTab] || {}),
      [canonicalGroupKey]: { ...(prev[activeTab]?.[canonicalGroupKey] || {}), ...canonicalForCache },
    },
  }));
};

export const normalizeGSTResponse = (gst) => {
  if (!gst || !gst.valid) return null;
  const flat = {};
  const nKey = (key) =>
    key.includes("_") || key.includes(" ")
      ? key
          .replace(/[^a-zA-Z0-9 ]/g, " ")
          .split(" ")
          .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1)))
          .join("")
      : key;
  Object.entries(gst).forEach(([k, v]) => {
    if (typeof v !== "object" || v === null) flat[nKey(k)] = v;
  });
  if (gst.gstDetails)
    Object.entries(gst.gstDetails).forEach(([k, v]) => {
      flat[nKey(k)] = v;
    });
  if (gst.address)
    Object.entries(gst.address).forEach(([k, v]) => {
      flat[nKey(k)] = v;
    });
  if (flat.gstStatus !== undefined) {
    flat.status = flat.gstStatus;
  }
  return flat;
};

const getStateId = (stateName, statesData) => {
  if (!stateName || !Array.isArray(statesData)) return stateName;
  const match = statesData.find((s) => s.Name?.toLowerCase() === stateName.toLowerCase());
  return match?.RecordID || stateName;
};

const getCountryId = (countryName, countriesData) => {
  if (!countryName || !Array.isArray(countriesData)) return countryName;
  const match = countriesData.find((c) => c.Name?.toLowerCase() === countryName.toLowerCase());
  return match?.RecordID || countryName;
};

export const mapGSTToFormValuesDynamic = (gstApiResponse, gFields, toCamelCaseFn, normalizeStaticSelectValueFn) => {
  const flatGST = normalizeGSTResponse(gstApiResponse);
  if (!flatGST) return {};
  const line1 = [flatGST.buildingNo, flatGST.buildingName, flatGST.flatNo].filter(Boolean).join(", ");

  const line2 = [flatGST.street, flatGST.locality, flatGST.district].filter(Boolean).join(", ");

  const city = flatGST.location || "";

  const formValues = {};
  const isInvoiceGroup = (name) => name?.toLowerCase().includes("invoic");
  const isDeliveryGroup = (name) => name?.toLowerCase().includes("deliver");
  let statesData = [];
  let countriesData = [];
  try {
    statesData = JSON.parse(localStorage.getItem("statesData") || "[]");
  } catch (e) {
    /* ignore */
  }
  try {
    countriesData = JSON.parse(localStorage.getItem("countriesData") || "[]");
  } catch (e) {
    /* ignore */
  }
  gFields.forEach((group) => {
    const groupId = group.groupId ? String(group.groupId).toLowerCase() : null;
    const groupName = group.groupName || "";
    const isAddressGroup = isInvoiceGroup(groupName) || isDeliveryGroup(groupName);
    group.fields.forEach((field) => {
      if (!field.visible) return;
      const fieldKey = toCamelCaseFn(field.name);
      let value = null;
      if (fieldKey === "companyName") value = flatGST.tradeName;
      if (fieldKey === "gstNo") value = flatGST.gstNo;
      if (isAddressGroup) {
        if (fieldKey === "state") value = getStateId(flatGST.state, statesData);
        if (fieldKey === "pincode") value = flatGST.pincode;
        if (fieldKey === "fullAddress") value = flatGST.fullAddress;
        if (fieldKey === "line1") value = line1;
        if (fieldKey === "line2") value = line2;
        if (fieldKey === "city") value = city;
        if (fieldKey === "companyType") {
          value = normalizeStaticSelectValueFn(flatGST.constitutionOfBusiness, field.defaultValue);
        }
        if (fieldKey === "country") value = getCountryId(flatGST.country || "India", countriesData);
      }
      if (value === null && flatGST[fieldKey] !== undefined) value = flatGST[fieldKey];
      if (value !== null && value !== undefined && value !== "") {
        if (groupId) {
          if (!formValues[groupId]) formValues[groupId] = {};
          formValues[groupId][fieldKey] = value;
        } else {
          formValues[fieldKey] = value;
        }
      }
    });
  });
  return formValues;
};

export const mapApiResponseToFormFields = (apiData, gFields, toCamelCaseFn, normalizeStaticSelectValueFn) => {
  const mappedValues = {};
  if (!apiData) return mappedValues;
  const lookup = {};
  const addToLookup = (obj) => {
    Object.keys(obj || {}).forEach((key) => {
      const v = obj[key];
      if (v === null || v === undefined) return;
      lookup[normalizeKey(key)] = v;
    });
  };
  addToLookup(apiData);
  Object.keys(apiData).forEach((key) => {
    if (/^group_\d+$/.test(key) && typeof apiData[key] === "object") addToLookup(apiData[key]);
  });
  gFields.forEach((group) => {
    group.fields.forEach((field) => {
      const normalizedField = normalizeKey(field.name);
      let value = lookup[normalizedField];
      if (value === undefined) return;
      const fieldKey = toCamelCaseFn(field.name);
      if (field.type === "select" && field.dataSource?.type === "static" && field.defaultValue) {
        value = normalizeStaticSelectValueFn(value, field.defaultValue);
      }
      if (group.groupId) {
        mappedValues[group.groupId] = mappedValues[group.groupId] || {};
        mappedValues[group.groupId][fieldKey] = value;
      } else {
        mappedValues[fieldKey] = value;
      }
    });
  });
  return mappedValues;
};

export const buildGroupedFields = (retailCustomerGroups, retailCustomerFields, activeTab, activeTabName, isB2BEnabled) => {
  const grouped = retailCustomerGroups
    .filter((group) => group.tabId === activeTab && group.visible)
    .sort((a, b) => a.order - b.order)
    .map((group) => {
      const fieldsInGroup = retailCustomerFields
        .filter((field) => {
          const isVisible = field.visible;
          const isCurrentTab = field.tabId === activeTab;
          const isBusinessTab = activeTabName.toLowerCase() === "business";
          const allowBusinessFields = !isBusinessTab || (isBusinessTab && isB2BEnabled);
          const isInGroup = field.groupId === group.id;
          return isVisible && isCurrentTab && allowBusinessFields && isInGroup;
        })
        .sort((a, b) => a.order - b.order);
      return {
        groupId: group.id,
        groupName: group.name || "",
        fields: fieldsInGroup,
        collapsible: group.collapsible,
        defaultExpanded: group.defaultExpanded,
        description: group.description,
      };
    });

  const ungroupedFields = retailCustomerFields
    .filter((field) => {
      const isVisible = field.visible;
      const isCurrentTab = field.tabId === activeTab;
      const isBusinessTab = activeTabName.toLowerCase() === "business";
      const allowBusinessFields = !isBusinessTab || (isBusinessTab && isB2BEnabled);
      const isUngrouped = !field.groupId;
      return isVisible && isCurrentTab && allowBusinessFields && isUngrouped;
    })
    .sort((a, b) => a.order - b.order);

  if (ungroupedFields.length > 0) {
    grouped.unshift({
      groupId: "",
      groupName: "",
      fields: ungroupedFields,
      collapsible: false,
      defaultExpanded: true,
      description: "",
    });
  }

  return grouped.filter((group) => group.fields.length > 0);
};
